import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  Button,
  Stack,
  useTheme,
  Drawer, // Added Drawer
  IconButton, // Added IconButton
  useMediaQuery, // Added useMediaQuery
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import MenuIcon from '@mui/icons-material/Menu'; // Added MenuIcon
import { mockConversations, Conversation, ChatMessage } from '../utils/mockData';

// Import new components
import ConversationListItem from '../components/chat/ConversationListItem';
import ChatMessageView from '../components/chat/ChatMessageView';
import ChatInput from '../components/chat/ChatInput';

const ChatPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [conversations, setConversations] = useState<Conversation[]>(mockConversations);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(
    conversations.length > 0 ? conversations[0] : null
  );
  const [currentMessages, setCurrentMessages] = useState<ChatMessage[]>(selectedConversation?.messages || []);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCurrentMessages(selectedConversation?.messages || []);
    if (isMobile) { // Close drawer when a conversation is selected on mobile
        setMobileDrawerOpen(false);
    }
  }, [selectedConversation, isMobile]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentMessages]);

  const handleSelectConversation = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    setSelectedConversation(conversation || null);
    // Drawer closing is handled by useEffect on selectedConversation change
  };

  const handleSendMessage = (messageText: string) => {
    if (!selectedConversation) {
        handleNewChat(messageText);
        return;
    }

    const newUserMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
    };

    // Simulate assistant response
    const assistantResponse: ChatMessage = {
      id: `asst-${Date.now()}`,
      text: `I received your message: "${messageText}". How can I assist further? (mock response)`,
      sender: 'assistant',
      timestamp: new Date(Date.now() + 1000), // 1 second later
    };

    const updatedMessages = [...(selectedConversation.messages || []), newUserMessage, assistantResponse];

    const updatedConversation: Conversation = {
      ...selectedConversation,
      messages: updatedMessages,
      lastUpdated: assistantResponse.timestamp,
    };

    const updatedConversationsList = conversations.map(c =>
      c.id === selectedConversation.id ? updatedConversation : c
    );

    setConversations(updatedConversationsList);
    setSelectedConversation(updatedConversation); // Keep the current conversation selected with new messages
    setCurrentMessages(updatedConversation.messages); // Update messages state immediately
  };

  const handleNewChat = (firstMessageText?: string) => {
    const newConvoId = `convo-${Date.now()}`;
    const initialMessages: ChatMessage[] = [];
    let title = "New Chat";

    if (firstMessageText) {
        initialMessages.push({
            id: `user-${Date.now()}`,
            text: firstMessageText,
            sender: 'user',
            timestamp: new Date()
        });
        // Simulate immediate assistant response for a new chat started with a message
        initialMessages.push({
            id: `asst-${Date.now()+1000}`,
            text: `Thanks for starting a new chat with: "${firstMessageText}". How can I help? (mock response)`,
            sender: 'assistant',
            timestamp: new Date(Date.now() + 1000)
        });
        title = firstMessageText.substring(0, 30) + (firstMessageText.length > 30 ? "..." : "");
    } else {
         initialMessages.push({
            id: `asst-${Date.now()}`,
            text: 'Hi! How can I help you today? (mock response)',
            sender: 'assistant',
            timestamp: new Date()
        });
    }

    const newConvo: Conversation = {
        id: newConvoId,
        title: title,
        messages: initialMessages,
        lastUpdated: initialMessages.length > 0 ? initialMessages[initialMessages.length -1].timestamp : new Date(),
    };
    setConversations(prev => [newConvo, ...prev]);
    setSelectedConversation(newConvo);
    setCurrentMessages(newConvo.messages);
    if (isMobile) {
        setMobileDrawerOpen(false);
    }
  };

  const conversationListContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
          }}
      >
        <Typography variant="h6" component="h2" fontWeight={600}>
          Chats
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddCommentOutlinedIcon />}
          onClick={() => handleNewChat()}
          size="small"
        >
          New Chat
        </Button>
      </Stack>
      <List sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
        {conversations.map((convo) => (
          <ConversationListItem
            key={convo.id}
            conversation={convo}
            isSelected={selectedConversation?.id === convo.id}
            onSelect={handleSelectConversation}
          />
        ))}
      </List>
    </Box>
  );

  return (
    <Box
        sx={{
            display: 'flex',
            // Adjusted height to be more robust, assuming ChatPage itself is the direct child filling viewport under a header.
            // If nested deeper, context from Layout would be better.
            height: {
                xs: 'calc(100vh - 56px)', // Typical mobile header height
                sm: 'calc(100vh - 64px)'  // Typical desktop header height
            },
            overflow: 'hidden',
        }}
    >
      <Grid container sx={{ flexGrow: 1 }}>
        {/* Left Column: Conversation List (Desktop) or Hidden (Mobile) */}
        {!isMobile && (
            <Grid
                item
                sm={4}
                md={3}
                sx={{
                    borderRight: `1px solid ${theme.palette.divider}`,
                    height: '100%',
                    bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800], // Slightly adjusted bg
                }}
            >
                {conversationListContent}
            </Grid>
        )}

        {/* Right Column: Main Chat Area */}
        <Grid
          item
          xs={12}
          sm={isMobile ? 12 : 8} // Full width on mobile if drawer is used, otherwise 8/12
          md={isMobile ? 12 : 9} // Full width on mobile if drawer is used, otherwise 9/12
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.default,
          }}
        >
          {/* Inner Box for actual content of Chat Area */}
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {selectedConversation ? (
              <>
                {/* Chat Area Header */}
                <Paper
                    elevation={0}
                    square
                    sx={{
                        // Adjusted padding for mobile header for more space
                        px: { xs: 1, sm: 2 },
                        py: { xs: 1.5, sm: 2 },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                  {isMobile && (
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      edge="start"
                      onClick={() => setMobileDrawerOpen(true)}
                      sx={{ mr: { xs: 1, sm: 2 } }} // Reduced margin on very small screens
                    >
                      <MenuIcon />
                    </IconButton>
                  )}
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"} // Slightly smaller title on mobile
                    component="h2"
                    fontWeight={500}
                    noWrap
                    sx={{flexGrow: 1}}
                  >
                    {selectedConversation.title}
                  </Typography>
                </Paper>

                {/* Message Display Area */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        p: { xs: 1.5, sm: 2, md: 3 } // Responsive padding
                    }}
                >
                  {currentMessages.map((msg) => (
                    <ChatMessageView message={msg} key={msg.id} />
                  ))}
                  <div ref={messagesEndRef} /> {/* Anchor for scrolling */}
                </Box>

                {/* Message Input Area - ChatInput handles its own padding and border */}
                <ChatInput
                    onSendMessage={handleSendMessage}
                    disabled={!selectedConversation && conversations.some(c => c.title === "New Chat" && c.messages.length === 0)}
                />
              </>
            ) : (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p:2 }}>
                <Typography variant="h6" color="text.secondary" textAlign="center">
                  Select a conversation or start a new one to begin chatting.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      {/* Mobile Drawer for Conversation List */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: {
            width: {xs: '280px', sm: '300px'}, // Slightly adjusted fixed width for drawer
            boxSizing: 'border-box',
        }}}
      >
        {conversationListContent}
      </Drawer>
    </Box>
  );
};

export default ChatPage;
